// React
import React from 'react';
import {useState, useCallback, useEffect, useRef} from 'react';

// MUI
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';

// others
import { useDrop } from 'react-dnd';
import { ReactFlow, useNodesState, useEdgesState, useViewport, addEdge} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useAuth } from 'react-oidc-context';

import './App.css';
import CustomDragLayer from './CustomDragLayer.jsx';
import FlowPaletteBox from './FlowPaletteBox.jsx';
import {PhProperties, PhPropertiesExtension} from './properties/PhProperties.jsx';
import {EcProperties, EcPropertiesExtension} from './properties/EcProperties.jsx';
import {TempProperties, TempPropertiesExtension} from './properties/TempProperties.jsx';
import {HumidityProperties, HumidityPropertiesExtension} from './properties/HumidityProperties.jsx';
import {Co2Properties, Co2PropertiesExtension} from './properties/Co2Properties.jsx';
import {PumpProperties, PumpPropertiesExtension} from './properties/PumpProperties.jsx';
import {ValveProperties, ValvePropertiesExtension} from './properties/ValveProperties.jsx';
import {FanProperties, FanPropertiesExtension} from './properties/FanProperties.jsx';
import {PowerProperties, PowerPropertiesExtension} from './properties/PowerProperties.jsx';
import {RelayProperties, RelayPropertiesExtension} from './properties/RelayProperties.jsx';
import {GtProperties, GtPropertiesExtension} from './properties/GtProperties.jsx';
import {LtProperties, LtPropertiesExtension} from './properties/LtProperties.jsx';
import {TimerProperties, TimerPropertiesExtension} from './properties/TimerProperties.jsx';
import {DelayProperties, DelayPropertiesExtension} from './properties/DelayProperties.jsx';
import {DailyProperties, DailyPropertiesExtension} from './properties/DailyProperties.jsx';
import { DragItemTypes } from './DragItemTypes.jsx';
import { useFetch } from './hooks/useFetch.jsx';
import * as fbproto from './messages_pb.js';    // this imports correctly, but autocomplete doesn't work
// import './messages_pb.js';      // this import works for autocomplete, but doesn't actually import correctly
// let fbproto = proto.fbproto;    // not sure why my IDE can only detect/autocomplete 'proto.fbproto' correctly and not 'fbproto', so this is a workaround for autocomplete. but this doesn't actually compile because the import doesn't work

function composeDeviceDisplayName(device) {
    let displayName = device.deviceAssemblyName;
    if (device.deviceName !== null && device.deviceName !== undefined && device.deviceName !== "" && device.deviceName !== device.deviceAssemblyName) {
        displayName = displayName + ", " + device.deviceName;
    }
    return displayName;
}

export const Flow = (props) => {
    const [leftOperatorBoxes] = useState([
        {operatorkey:'a', nodeType:fbproto.NodeType.NT_GT, top:1, left:20, width:30, height:30, title:">", extension:GtPropertiesExtension},
        {operatorkey:'b', nodeType:fbproto.NodeType.NT_LT, top:170, left:20, width:30, height:30, title:"<", extension:LtPropertiesExtension},
        {operatorkey:'c', nodeType:fbproto.NodeType.NT_TIMER, top:170, left:20, width:70, height:30, title:"timer", extension:TimerPropertiesExtension},
        {operatorkey:'d', nodeType:fbproto.NodeType.NT_DELAY, top:170, left:20, width:70, height:30, title:"delay", extension:DelayPropertiesExtension},
        {operatorkey:'e', nodeType:fbproto.NodeType.NT_DAILY_ON, top:170, left:20, width:70, height:30, title:"daily (on)", extension:DailyPropertiesExtension},
        {operatorkey:'f', nodeType:fbproto.NodeType.NT_DAILY_ENABLE, top:170, left:20, width:70, height:30, title:"daily (enable)", extension:DailyPropertiesExtension},
    ]);
    const [leftDeviceBoxes, setLeftDeviceBoxes] = useState([]);
    const [canvasBoxes, setCanvasBoxes, onCanvasBoxesChange] = useNodesState([
//        {id:'n', type:'default', position:{x:150, y:100}, data:{label:'EC sensor A'}, fb:{nodeType:fbproto.NodeType.NT_EC, top: 380, left: 150, width:150, height:30, title: "EC sensor"}},
//        {id:'m', type:'default', position:{x:450, y:100}, data:{label:'pH sensor A'}, fb:{nodeType:fbproto.NodeType.NT_PH, top: 300, left: 450, width:150, height:30, title: "pH sensor"}},
//        {id:'o', type:'default', position:{x:150, y:280}, data:{label:'>'}, fb: {nodeType:fbproto.NodeType.NT_GT, top: 200, left: 150, width:30, height:30, title: ">"}},
//        {id:'p', type:'default', position:{x:450, y:380}, data:{label:'pump A'}, fb: {nodeType:fbproto.NodeType.NT_PUMP, top: 100, left: 450, width:150, height:30, title: "Pump A"}},
    ]);
    const [canvasConnectors, setCanvasConnectors, onCanvasConnectorsChange] = useEdgesState([
//        {id:'x', source:'o', target:'p', fb:{}},
//        {id:'y', source:'m', target:'o', fb:{}},
//        {id:'z', source:'n', target:'p', fb:{}},
    ]);
    const [selectedItem, setSelectedItem] = useState(null);
    const canvasRef = useRef();
    const operatorsRef = useRef();
    const devicesRef = useRef();
    const tempRef = useRef({}); tempRef.current = {canvasBoxes:canvasBoxes, canvasConnectors:canvasConnectors, leftDeviceBoxes:leftDeviceBoxes};
    const canvasViewport = useViewport();
    
    const fetchWrapper = useFetch();
    const auth = useAuth();

    useEffect(() => {
        // fetch devices
        const userIdObj = {userId: auth.user.profile.sub};
        const devicesPromise = fetchWrapper.post(`https://api.${process.env.REACT_APP_DOMAIN}/devices/userid`, userIdObj)
            .then(response => {
                return response.json();
            })
            .then(responseObj => {
                // sample response: {"devices":[{"id":2,"controllerId":4,"deviceType":2,"deviceName":"some EC probe","deviceAssemblyName":"some EC probe"}, ...]}
                console.log("get devices body = " + JSON.stringify(responseObj));
                
                // store devices in a displayable format
                var myDevices = [];
                for (const device of responseObj.devices) {
                    let nodeType = getNodeTypeForDeviceType(device.deviceType);
                    var newDevice = {
                        id: device.id,
                        nodeType: nodeType,
                        top:20, left:20, width:150, height:30, 
                        title: composeDeviceDisplayName(device), 
                        extension: getExtensionForNodeType(nodeType),
                    };
                    myDevices.push(newDevice);
                }
                setLeftDeviceBoxes(myDevices);
                return myDevices;
            })
            .catch(error => {
                console.log("error getting user's devices: " + error);
                return null;
            });
        
        const flowPromise = fetchWrapper.post(`https://api.${process.env.REACT_APP_DOMAIN}/flow/userid`, userIdObj)
            .then(response => {
                return response.json();
            })
            .then(responseObj => {
                // sample response: {
                //   "flow": {
                //     "id": 1, "ownerId": 1, "dateCreated": "2024-09-27T15:47:18.988446Z", "dateUpdated": "2024-09-27T15:47:56.19521Z",
                //     "nodes" <will be properly escaped>: "[{"fb": {"deviceId": 1, "nodeType": 1, "pollPeriod": 30}, "id": "0", "data": {"label": "virtual pH probe"}, "type": "default", "measured": {"width": 150, "height": 36}, "position": {"x": 401.08792460316533, "y": 145.73544913690213}}, {"fb": {"nodeType": 12, "runEvery": 30}, "id": "1", "data": {"label": "timer"}, "type": "default", "dragging": false, "measured": {"width": 150, "height": 36}, "position": {"x": 409.98636210316533, "y": 19.485449136902133}, "selected": false}]",
                //     "edges" <will be properly escaped>: "[{"id": "xy-edge__1-0", "source": "1", "target": "0"}]"
                //   },
                //   "error": 0
                // }
                console.log("get flow body = " + JSON.stringify(responseObj));
                
                // restore flow
                let nodes = JSON.parse(responseObj.flow.nodes)
                let edges = JSON.parse(responseObj.flow.edges)
                return {
                    nodes: Array.isArray(nodes) ? nodes : null,
                    edges: Array.isArray(edges) ? edges : null,
                };
            })
            .catch(error => {
                console.log("error getting user's flow");
                return null;
            });
        
        // device node labels are stored snapshots; re-derive them from the
        // current device names so renames show up without a re-save
        Promise.all([devicesPromise, flowPromise]).then(([myDevices, flow]) => {
            if (!flow) {
                return;
            }
            let nodes = flow.nodes;
            if (Array.isArray(nodes) && Array.isArray(myDevices)) {
                const titleByDeviceId = {};
                for (const device of myDevices) {
                    titleByDeviceId[device.id] = device.title;
                }
                nodes = nodes.map(node => {
                    const deviceId = node.fb && node.fb.deviceId;
                    if (deviceId !== null && deviceId !== undefined && titleByDeviceId[deviceId] !== undefined) {
                        return {...node, data: {...node.data, label: titleByDeviceId[deviceId]}};
                    }
                    return node;
                });
            }
            if (Array.isArray(nodes))
                setCanvasBoxes(nodes);
            if (flow.edges)
                setCanvasConnectors(flow.edges);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    
    function getNodeTypeForDeviceType(deviceType) {
        // alternatively, directly define each FlowNodeTypes to be equal to the corresponding 
        //  fbproto.DeviceType, but there might be clashes for the ones that have no correspondant type
        switch (deviceType) {
            case fbproto.DeviceType.DT_NONE:
                return fbproto.NodeType.NT_NONE;
            case fbproto.DeviceType.DT_PH:
                return fbproto.NodeType.NT_PH;
            case fbproto.DeviceType.DT_EC:
                return fbproto.NodeType.NT_EC;
            case fbproto.DeviceType.DT_TEMPERATURE:
                return fbproto.NodeType.NT_TEMPERATURE;
            case fbproto.DeviceType.DT_HUMIDITY:
                return fbproto.NodeType.NT_HUMIDITY;
            case fbproto.DeviceType.DT_CO2:
                return fbproto.NodeType.NT_CO2;
            case fbproto.DeviceType.DT_PUMP:
                return fbproto.NodeType.NT_PUMP;
            case fbproto.DeviceType.DT_VALVE:
                return fbproto.NodeType.NT_VALVE;
            case fbproto.DeviceType.DT_FAN:
                return fbproto.NodeType.NT_FAN;
            case fbproto.DeviceType.DT_POWER:
                return fbproto.NodeType.NT_POWER;
            case fbproto.DeviceType.DT_RELAY:
                return fbproto.NodeType.NT_RELAY;
            default:
                return undefined;
        }
    }

    function getExtensionForNodeType(nodeType) {
        switch (nodeType) {
            case fbproto.NodeType.NT_PH:
                return PhPropertiesExtension;
            case fbproto.NodeType.NT_EC:
                return EcPropertiesExtension;
            case fbproto.NodeType.NT_TEMPERATURE:
                return TempPropertiesExtension;
            case fbproto.NodeType.NT_HUMIDITY:
                return HumidityPropertiesExtension;
            case fbproto.NodeType.NT_CO2:
                return Co2PropertiesExtension;
            case fbproto.NodeType.NT_PUMP:
                return PumpPropertiesExtension;
            case fbproto.NodeType.NT_VALVE:
                return ValvePropertiesExtension;
            case fbproto.NodeType.NT_FAN:
                return FanPropertiesExtension;
            case fbproto.NodeType.NT_POWER:
                return PowerPropertiesExtension;
            case fbproto.NodeType.NT_RELAY:
                return RelayPropertiesExtension;
            case fbproto.NodeType.NT_GT:
                return GtPropertiesExtension;
            case fbproto.NodeType.NT_LT:
                return LtPropertiesExtension;
            case fbproto.NodeType.NT_TIMER:
                return TimerPropertiesExtension;
            case fbproto.NodeType.NT_DELAY:
                return DelayPropertiesExtension;
            case fbproto.NodeType.NT_DAILY_ON:
                return DailyPropertiesExtension;
            case fbproto.NodeType.NT_DAILY_ENABLE:
                return DailyPropertiesExtension;
            default:
                return undefined;
        }
    }

    function isDailyOnDriven(node, edges, nodes) {
        // True iff this actuator has at least one incoming edge from an
        // NT_DAILY_ON node. NT_DAILY_ENABLE does NOT qualify — it only
        // toggles the `enabled` gate and does NOT lock runFor to a 3s
        // watchdog (the actuator keeps its configured runFor and runs when
        // its OTHER triggers fire, gated by enabled).
        if (!node || !node.fb || node.id == null) return false;
        const isActuator = node.fb.nodeType === fbproto.NodeType.NT_PUMP
            || node.fb.nodeType === fbproto.NodeType.NT_VALVE
            || node.fb.nodeType === fbproto.NodeType.NT_FAN
            || node.fb.nodeType === fbproto.NodeType.NT_RELAY;
        if (!isActuator) return false;
        for (const e of edges) {
            if (e.target === node.id) {
                const src = nodes.find(n => n.id === e.source);
                if (src && src.fb && src.fb.nodeType === fbproto.NodeType.NT_DAILY_ON) return true;
            }
        }
        return false;
    }

    function isDailyOnOnlyDriven(node, edges, nodes) {
        // True iff the actuator has at least one NT_DAILY_ON -> this edge AND
        // every incoming edge is from an NT_DAILY_ON node. Used by the
        // actuator property panels to decide between "locked 3s watchdog"
        // (only-driven) and "editable runFor with conflict warning" (driven
        // but mixed sources). NT_DAILY_ENABLE edges count as "other" for
        // this purpose — they don't lock runFor but DO coexist, so a
        // DAILY_ON + DAILY_ENABLE mix keeps runFor editable with a warning.
        if (!node || !node.fb || node.id == null) return false;
        const isActuator = node.fb.nodeType === fbproto.NodeType.NT_PUMP
            || node.fb.nodeType === fbproto.NodeType.NT_VALVE
            || node.fb.nodeType === fbproto.NodeType.NT_FAN
            || node.fb.nodeType === fbproto.NodeType.NT_RELAY;
        if (!isActuator) return false;
        let hasDailyOn = false;
        let hasOther = false;
        for (const e of edges) {
            if (e.target === node.id) {
                const src = nodes.find(n => n.id === e.source);
                if (src && src.fb) {
                    if (src.fb.nodeType === fbproto.NodeType.NT_DAILY_ON) hasDailyOn = true;
                    else hasOther = true;
                }
            }
        }
        return hasDailyOn && !hasOther;
    }
    
    let intKey = 1;   // start at 1: 0 is a valid id but collides with value-initialized device nodes on the firmware, so avoid generating it
    // generates next n unique keys not found in arr, where arr is an array of objects, each containing a key 'id'
    function generateNextUniqueKey(arr, n=1) {
        let retval = [];
        for (let i = 0; i < n; i++) {
            let found = true;
            while (found) {
                found = false;
                for (let obj of arr) {
                    if (obj.id === intKey.toString()) {
                        found = true;
                        break;
                    }
                }
                for (let obj of retval) {
                    if (found) break;
                    if (obj.id === intKey.toString()) {
                        found = true;
                        break;
                    }
                }
                if (!found) retval.push(intKey);
                intKey++;
            }
        }
        if (retval.length === 1)
            return retval[0];
        return retval;
    }
    const copyPaletteBox = (deviceId, nodeType, x, y, width, height, title, extension) => {
        let nextKey = generateNextUniqueKey(canvasBoxes, 1);
        //console.log("creating box id " + nextKey + " location ("+x+","+y+")");
        const newNode = {id:nextKey.toString(), type:'default', position:{x, y}, data:{label:title}, fb:{...extension, nodeType, deviceId}};
        setCanvasBoxes((nodes) => nodes.concat(newNode));
    };
    const [, canvasDropRef] = useDrop(
        () => ({
            accept: [DragItemTypes.PALETTE_BOX],
            drop(item, monitor) {
                // console.log("useDrop, type " + item.dragItemType);
                if (item.dragItemType === DragItemTypes.PALETTE_BOX) {
                    // Translate dragged item coordinates to reactflow canvas coordinates.
                    // There are 2 coordinate systems: browser window, and reactflow internal. Reactflow maintains its own coordinate 
                    //   system, and the canvas provides a viewport into that world
                    // note: x and y all refer to top-left points
                    
                    // Reactflow canvas rectangle in browser window coordinates
                    const canvasRectInScreen = canvasRef.current.getBoundingClientRect();
                    // location of the dragged item's top-left corner when it was dropped, in browser window coordinates
                    const itemDropCoordsInScreen = monitor.getSourceClientOffset();
                    // console.log("useDrop, canvasRectInScreen = " + JSON.stringify(canvasRectInScreen));
                    // console.log("useDrop, canvasViewport = " + JSON.stringify(canvasViewport));
                    
                    // note: canvasViewport refers to top-left point of the viewport in the reactflow coordinate system. the canvasViewport x and y is actually scaled by -1 and canvasViewport.zoom
                    // location of reactflow canvas' viewport's top-left corner in reactflow's coordinate system. i.e., where was the viewport looking at in reactflow's internal world.
                    const viewportXInRflow = -(canvasViewport.x / canvasViewport.zoom);
                    const viewportYInRflow = -(canvasViewport.y / canvasViewport.zoom);
                    // location of the dragged item's top-left corner when it was dropped, relative to reactflow canvas. i.e., where was the item dropped on the canvas.
                    const dropXInCanvasRect = (itemDropCoordsInScreen.x - canvasRectInScreen.x);
                    const dropYInCanvasRect = (itemDropCoordsInScreen.y - canvasRectInScreen.y);
                    // console.log("useDrop, dropInCanvasRect = " + dropXInCanvasRect + ", " + dropYInCanvasRect);
                    // location of the dragged item's top-left corner when it was dropped, in reactflow's coordinate system
                    const dropXInRflow = viewportXInRflow + (dropXInCanvasRect / canvasViewport.zoom);
                    const dropYInRflow = viewportYInRflow + (dropYInCanvasRect / canvasViewport.zoom);
                    copyPaletteBox(item.id, item.nodeType, dropXInRflow, dropYInRflow, item.width, item.height, item.title, item.extension);
                }
                return undefined;
            }
        }),
        [canvasBoxes, canvasViewport]
    );
    const onConnect = useCallback(
        (newEdge) => {
            setCanvasConnectors((edges) => {
                const updatedEdges = addEdge(newEdge, edges);
                // Detect multi-source conflicts on actuators BEFORE mutating
                // runFor, so the watchdog auto-set never silently overwrites
                // a runFor that another trigger source (e.g. an NT_TIMER with
                // runFor=300) is relying on. The actuator's single runFor
                // field cannot simultaneously mean "watchdog 3s" for daily
                // AND "fixed run duration" for another source.
                setCanvasBoxes((nodes) => {
                    const srcNode = nodes.find(n => n.id === newEdge.source);
                    const tgtNode = nodes.find(n => n.id === newEdge.target);
                    if (!srcNode || !tgtNode || !srcNode.fb || !tgtNode.fb) return nodes;
                    const isActuatorTarget = tgtNode.fb.nodeType === fbproto.NodeType.NT_PUMP
                        || tgtNode.fb.nodeType === fbproto.NodeType.NT_VALVE
                        || tgtNode.fb.nodeType === fbproto.NodeType.NT_FAN
                        || tgtNode.fb.nodeType === fbproto.NodeType.NT_RELAY;
                    if (!isActuatorTarget) return nodes;
                    const srcIsDaily = srcNode.fb.nodeType === fbproto.NodeType.NT_DAILY_ON;
                    const incomingEdges = updatedEdges.filter(e => e.target === tgtNode.id);
                    const hasOtherIncoming = incomingEdges.some(e => {
                        const s = nodes.find(n => n.id === e.source);
                        return s && s.fb && s.fb.nodeType !== fbproto.NodeType.NT_DAILY_ON && e.source !== newEdge.source;
                    });
                    if (srcIsDaily && !hasOtherIncoming) {
                        // NT_DAILY_ON is the sole source: lock runFor to 3s
                        // watchdog. (NT_DAILY_ENABLE is treated as "other" —
                        // adding a DAILY_ENABLE edge does NOT lock runFor.)
                        const prevRunFor = tgtNode.fb.runFor;
                        if (prevRunFor !== 3) tgtNode.fb._prevRunFor = prevRunFor;
                        tgtNode.fb.runFor = 3;
                        return nodes.map(n => n.id === tgtNode.id ? {...n} : n);
                    }
                    if (srcIsDaily && hasOtherIncoming) {
                        // Mixed sources: do NOT overwrite the existing runFor.
                        // The Daily(On) watchdog requires runFor to be short
                        // (~3s), but the other source expects its own runFor.
                        // There is no single value that satisfies both, so we
                        // warn and let the user pick a compromise (or remove
                        // one edge).
                        console.warn(
                            `Daily(On) edge into actuator ${tgtNode.id} which already has non-Daily(On) sources. ` +
                            `runFor left at ${tgtNode.fb.runFor}s. ` +
                            `Daily(On) watchdog requires ~3s; conflicting source may require longer. ` +
                            `Set runFor manually or remove one of the conflicting edges.`
                        );
                    }
                    else if (!srcIsDaily && isDailyOnDriven(tgtNode, updatedEdges, nodes)) {
                        // Adding a non-Daily(On) edge to an actuator already
                        // driven by a Daily(On) node. The existing 3s watchdog
                        // will now truncate runs triggered by this new source
                        // — warn. (NT_DAILY_ENABLE edges also reach this
                        // branch — they are not "srcIsDaily", and an actuator
                        // already locked by DAILY_ON having a new DAILY_ENABLE
                        // edge added just produces the same truncation warning,
                        // which is correct: the 3s watchdog still applies.)
                        console.warn(
                            `Non-Daily(On) edge into actuator ${tgtNode.id} which is already driven by a Daily(On) node ` +
                            `(runFor=${tgtNode.fb.runFor}s watchdog). Daily(On)'s watchdog may truncate runs from the new source. ` +
                            `Consider removing the Daily(On) edge, or be aware that actuator runs from the new source will turn off within ~${tgtNode.fb.runFor}s unless refreshed by the Daily(On) window.`
                        );
                    }
                    return nodes;
                });
                return updatedEdges;
            });
        },
        [setCanvasBoxes, setCanvasConnectors]
    );
    
    // clicks
    const handleEmptyClick = useCallback(
        (e) => {
            console.log(`clicked nothing`);
            setSelectedItem(null);
        },
        []
    );
    const handleClick = useCallback(
        (e, obj) => {
            console.log(`clicked, obj = ${JSON.stringify(obj)}`);
            setSelectedItem(obj);
            e.stopPropagation();
        },
        []
    );
    function autodoserClicked() {
        console.log("autodoser template clicked");
        //console.log(`canvasBoxes = ${JSON.stringify(canvasBoxes)}`);
        let newIds = generateNextUniqueKey(canvasBoxes, 12).map((intId) => intId.toString());
        let newCanvasBoxes = [];
        newCanvasBoxes.push({id:newIds[0], type:'default', position:{x:80, y:0}, data:{label:'EC sensor A'}, fb:{nodeType:fbproto.NodeType.NT_EC, pollPeriod:10}});
        newCanvasBoxes.push({id:newIds[1], type:'default', position:{x:370, y:0}, data:{label:'pH sensor A'}, fb:{nodeType:fbproto.NodeType.NT_PH, pollPeriod:20}});
        newCanvasBoxes.push({id:newIds[2], type:'default', position:{x:370, y:80}, data:{label:'>'}, fb:{nodeType:fbproto.NodeType.NT_GT, triggerValue:3.5}});
        newCanvasBoxes.push({id:newIds[3], type:'default', position:{x:80, y:80}, data:{label:'<'}, fb:{nodeType:fbproto.NodeType.NT_LT, triggerValue:1300}});
        newCanvasBoxes.push({id:newIds[4], type:'default', position:{x:10, y:160}, data:{label:'dosing pump A'}, fb:{nodeType:fbproto.NodeType.NT_PUMP, runFor:10}});
        newCanvasBoxes.push({id:newIds[5], type:'default', position:{x:180, y:160}, data:{label:'dosing pump B'}, fb:{nodeType:fbproto.NodeType.NT_PUMP, runFor:2}});
        newCanvasBoxes.push({id:newIds[6], type:'default', position:{x:370, y:160}, data:{label:'dosing pump C'}, fb:{nodeType:fbproto.NodeType.NT_PUMP, runFor:2}});
        setCanvasBoxes((nodes) => nodes.concat(newCanvasBoxes));
        
        let newCanvasConnectors = [];
        newCanvasConnectors.push({id:newIds[7], type:'default', source:newIds[0], target:newIds[3]});
        newCanvasConnectors.push({id:newIds[8], type:'default', source:newIds[3], target:newIds[4]});
        newCanvasConnectors.push({id:newIds[9], type:'default', source:newIds[3], target:newIds[5]});
        newCanvasConnectors.push({id:newIds[10], type:'default', source:newIds[1], target:newIds[2]});
        newCanvasConnectors.push({id:newIds[11], type:'default', source:newIds[2], target:newIds[6]});
        //console.log(`newIds = ${JSON.stringify(newIds)}`);
        //console.log(`newCanvasBoxes = ${JSON.stringify(newCanvasBoxes)}`);
        //console.log(`newCanvasConnectors = ${JSON.stringify(newCanvasConnectors)}`);
        setCanvasConnectors((edges) => edges.concat(newCanvasConnectors));
    };
    
    // keypresses
    function handleKeyPress(e) {
        console.log(`key pressed = ${e.key}`);
        if (e.key === "r") {
            // HACK: add devices to list
            // let devicesToAdd = [
            //     {id:'ba', nodeType:fbproto.NodeType.NT_EC, top:20, left:20, width:150, height:30, title:"EC sensor A", extension:EcPropertiesExtension},
            //     {id:'bb', nodeType:fbproto.NodeType.NT_PH, top:70, left:20, width:150, height:30, title:"pH sensor A", extension:PhPropertiesExtension},
            //     {id:'bc', nodeType:fbproto.NodeType.NT_PUMP, top:220, left:20, width:150, height:30, title:"dosing pump A", extension:PumpPropertiesExtension},
            //     {id:'bd', nodeType:fbproto.NodeType.NT_PUMP, top:220, left:20, width:150, height:30, title:"dosing pump B", extension:PumpPropertiesExtension},
            //     {id:'be', nodeType:fbproto.NodeType.NT_PUMP, top:220, left:20, width:150, height:30, title:"dosing pump C", extension:PumpPropertiesExtension},
            //     {id:'bf', nodeType:fbproto.NodeType.NT_PUMP, top:220, left:20, width:150, height:30, title:"water pump D", extension:PumpPropertiesExtension},
            //     {id:'bg', nodeType:fbproto.NodeType.NT_PUMP, top:220, left:20, width:150, height:30, title:"water pump E", extension:PumpPropertiesExtension},
            // ];
            // setLeftDeviceBoxes((oldDevices) => {
            //     return [].concat(oldDevices, devicesToAdd);
            // });
        }
        if (e.key === "p") {
            // print debug
            let canvasBoxes = tempRef.current.canvasBoxes;
            let canvasConnectors = tempRef.current.canvasConnectors;
            let leftDeviceBoxes = tempRef.current.leftDeviceBoxes;
            console.log(`canvasBoxes = ${JSON.stringify(canvasBoxes)}`);
            console.log(`canvasConnectors = ${JSON.stringify(canvasConnectors)}`);
            console.log('palette = ' + JSON.stringify(leftOperatorBoxes));
            console.log('l device = ' + JSON.stringify(leftDeviceBoxes));
        }
    };
    useEffect(() => {
        document.addEventListener('keyup', handleKeyPress);
        return function cleanup() {
            document.removeEventListener('keyup', handleKeyPress);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    
    const renderSelectedItemProperty = useCallback(
        () => {
            //console.log(`rendering property for ${JSON.stringify(selectedItem)}`);
            if (!selectedItem || !selectedItem.fb || !selectedItem.fb.nodeType) {
                return (null);
            }
            switch (selectedItem.fb.nodeType) {
                case fbproto.NodeType.NT_PH:
                    return (<PhProperties key={selectedItem.id} node={selectedItem} changeFunc={setSelectedItem}/>)
                case fbproto.NodeType.NT_EC:
                    return (<EcProperties key={selectedItem.id} node={selectedItem} changeFunc={setSelectedItem}/>)
                case fbproto.NodeType.NT_TEMPERATURE:
                    return (<TempProperties key={selectedItem.id} node={selectedItem} changeFunc={setSelectedItem}/>)
                case fbproto.NodeType.NT_HUMIDITY:
                    return (<HumidityProperties key={selectedItem.id} node={selectedItem} changeFunc={setSelectedItem}/>)
                case fbproto.NodeType.NT_CO2:
                    return (<Co2Properties key={selectedItem.id} node={selectedItem} changeFunc={setSelectedItem}/>)
                case fbproto.NodeType.NT_PUMP:
                    return (<PumpProperties key={selectedItem.id} node={selectedItem} changeFunc={setSelectedItem} dailyDriven={isDailyOnDriven(selectedItem, canvasConnectors, canvasBoxes)} dailyOnlyDriven={isDailyOnOnlyDriven(selectedItem, canvasConnectors, canvasBoxes)}/>)
                case fbproto.NodeType.NT_VALVE:
                    return (<ValveProperties key={selectedItem.id} node={selectedItem} changeFunc={setSelectedItem} dailyDriven={isDailyOnDriven(selectedItem, canvasConnectors, canvasBoxes)} dailyOnlyDriven={isDailyOnOnlyDriven(selectedItem, canvasConnectors, canvasBoxes)}/>)
                case fbproto.NodeType.NT_FAN:
                    return (<FanProperties key={selectedItem.id} node={selectedItem} changeFunc={setSelectedItem} dailyDriven={isDailyOnDriven(selectedItem, canvasConnectors, canvasBoxes)} dailyOnlyDriven={isDailyOnOnlyDriven(selectedItem, canvasConnectors, canvasBoxes)}/>)
                case fbproto.NodeType.NT_POWER:
                    return (<PowerProperties key={selectedItem.id} node={selectedItem} changeFunc={setSelectedItem}/>)
                case fbproto.NodeType.NT_RELAY:
                    return (<RelayProperties key={selectedItem.id} node={selectedItem} changeFunc={setSelectedItem} dailyDriven={isDailyOnDriven(selectedItem, canvasConnectors, canvasBoxes)} dailyOnlyDriven={isDailyOnOnlyDriven(selectedItem, canvasConnectors, canvasBoxes)}/>)
                case fbproto.NodeType.NT_GT:
                    return (<GtProperties key={selectedItem.id} node={selectedItem} changeFunc={setSelectedItem}/>)
                case fbproto.NodeType.NT_LT:
                    return (<LtProperties key={selectedItem.id} node={selectedItem} changeFunc={setSelectedItem}/>)
                case fbproto.NodeType.NT_TIMER:
                    return (<TimerProperties key={selectedItem.id} node={selectedItem} changeFunc={setSelectedItem}/>)
                case fbproto.NodeType.NT_DELAY:
                    return (<DelayProperties key={selectedItem.id} node={selectedItem} changeFunc={setSelectedItem}/>)
                case fbproto.NodeType.NT_DAILY_ON:
                    return (<DailyProperties key={selectedItem.id} node={selectedItem} changeFunc={setSelectedItem}/>)
                case fbproto.NodeType.NT_DAILY_ENABLE:
                    return (<DailyProperties key={selectedItem.id} node={selectedItem} changeFunc={setSelectedItem}/>)
                default:
                    return null;
            }
        },
        [selectedItem, canvasConnectors, canvasBoxes]
    );
    
    return (
        <Box className="Flow" sx={{overflow:'overlay', display:'flex', flexDirection:'row', width:'100%', height:'100%', borderTop:'1px solid', borderColor:'grey.300' }}>
            
            {/* left util bar, or palettes */}
            <Box sx={{overflow:'auto', position:'static', width:'15%', height:'100%', borderRight:'1px solid', borderColor:'grey.300'}}>
                
                {/* TODO: change to use Stack, similar to the templates section */}
                {/* Operator nodes */}
                <Box sx={{position:'static', display:'flex', flexDirection:'column', width:'100%'}}>
                    <Box sx={{position:'static', padding:'0.5em', borderBottom:'1px solid', borderColor:'grey.300'}}>Operators</Box>
                    <Box ref={operatorsRef} sx={{position:'static', display:'flex', flexDirection:'column', alignItems:'center', rowGap:'1em', padding:'1em', borderBottom:'1px solid', borderColor:'grey.300'}}>
                        {leftOperatorBoxes.map((obj) => (
                            <FlowPaletteBox key={obj.operatorkey} {...obj} />
                        ))}
                    </Box>
                </Box>

                {/* TODO: change to use Stack, similar to the templates section */}
                {/* Device nodes */}
                <Box sx={{position:'static', display:'flex', flexDirection:'column', width:'100%'}}>
                    <Box sx={{position:'static', padding:'0.5em', borderBottom:'1px solid', borderColor:'grey.300'}}>Devices</Box>
                    <Box ref={devicesRef} sx={{position:'static', display:'flex', flexDirection:'column', alignItems:'center', rowGap:'1em', padding:'1em', borderBottom:'1px solid', borderColor:'grey.300'}}>
                        {leftDeviceBoxes.map((obj) => (
                            <FlowPaletteBox key={obj.id} {...obj} />
                        ))}
                    </Box>
                </Box>
            </Box>
            
            {/* center area */}
            <Box sx={{flexGrow:'1', width:'70%', height:'100%', display:'flex', flexDirection:'column'}}>
                {/* top bar */}
                <Box sx={{height:'2em', display:'flex', flexDirection:'row', borderBottom:'1px solid', borderColor:'grey.300'}}>
                    <Box sx={{flexGrow:'1', margin:'auto'}}>
                        Create your flowchart below and click Save. {/* viewport: {canvasViewport.x}, {canvasViewport.y}, {canvasViewport.zoom} */}
                    </Box>
                    <Button sx={{margin:'4px', marginRight:'8px'}} variant="contained" onClick={() => {
                        // TODO: save api
                        const requestObj = {
                            userId: auth.userId,
                            nodes: JSON.stringify(canvasBoxes),
                            edges: JSON.stringify(canvasConnectors),
                        };
                        fetchWrapper.post(`https://api.${process.env.REACT_APP_DOMAIN}/flow/save`, requestObj)
                            .then(response => {
                                console.log(response.json())
                            });
                    }}>
                        Save
                    </Button>
                </Box>

                {/* main flow area */}
                <Box ref={canvasRef} sx={{position:'relative', width:'100%', height:'100%'}} onClick={(e) => handleEmptyClick(e)}>
                    <Box ref={canvasDropRef} sx={{width:'100%', height:'100%'}}>
                            <ReactFlow
                                nodes={canvasBoxes}
                                edges={canvasConnectors}
                                onNodesChange={onCanvasBoxesChange}
                                onEdgesChange={onCanvasConnectorsChange}
                                onConnect={onConnect}
                                onNodeClick={handleClick}
                                onNodeDragStart={handleClick}
                                onEdgeClick={handleClick}
                                fitView
                            />
                    </Box>
                </Box>
            </Box>
            

            {/* right util bar */}
            <Box sx={{overflow:'auto', position:'static', width:'15%', height:'100%', borderLeft:'1px solid', borderColor:'grey.300' }}>
                
                {/* Properties */}
                <Box sx={{position:'static', display:'flex', flexDirection:'column', width:'100%'}}>
                    <Box sx={{position:'static', padding:'0.5em', borderBottom:'1px solid', borderColor:'grey.300'}}>Properties</Box>
                    <Box sx={{position:'static', display:'flex', flexDirection:'column', alignItems:'center', rowGap:'1em', padding:'1em', borderBottom:'1px solid', borderColor:'grey.300'}}>
                        {renderSelectedItemProperty()} 
                        {/* renderSelectedItemPosition() */}
                    </Box>
                </Box>
                
                {/* Templates */}
                <Box sx={{position:'static', display:'flex', flexDirection:'column', width:'100%'}}>
                    <Box sx={{position:'static', padding:'0.5em', borderBottom:'1px solid', borderColor:'grey.300'}}>Templates</Box>
                    <Box sx={{position:'static', alignItems:'center', padding:'1em', borderBottom:'1px solid', borderColor:'grey.300'}}>
                        <Stack sx={{padding:'1em'}} spacing={'1em'} direction="column">
                            <Button variant="contained" disabled onClick={autodoserClicked}>Autodoser</Button>
                            <Button variant="contained" disabled onClick={autodoserClicked}>Flood-and-ebb controller</Button>
                            <Button variant="contained" disabled onClick={autodoserClicked}>Sprinkler</Button>
                            <Button variant="contained" disabled onClick={autodoserClicked}>Tank irrigation</Button>
                        </Stack>
                    </Box>
                </Box>
            </Box>

            <CustomDragLayer/>
            
        </Box>

    )
}

export default Flow;

